import ApiService from '@/lib/api';
import Fold from '@/components/Fold';
import { GetStaticProps } from 'next';
import MainLayout from '../layouts/MainLayout';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import ResponsiveGrid from '@/components/ui/ResponsiveGrid';

import imageUrlBuilder from '@sanity/image-url';
import sanityClient from '../lib/sanity';
import ContentBlock from '@/components/ContentBlock/ContentBlock';

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  const apiService = new ApiService();
  const [menuData, homeData, blogPosts, miscData] = await Promise.all([
    apiService.getMenuData(),
    apiService.getHomeData(preview),
    apiService.getThreeLatestBlogPosts(),
    apiService.getMiscData(),
  ]);
  const { footer } = miscData;
  return {
    props: {
      homeData,
      menuData,
      footer,
      blogPosts,
    },
    revalidate: 3600,
  };
};

const hardcodedData = {
  fold: {
    heading: 'Aim for indispensable.',
    image: { hardCoded: '/img/staples.png' },
  },
  banner1: {
    heading: 'Less Chaos. More Impact.',
    content:
      'We partner with senior executives and founders to provide a structured approach to strategy and development designed to meet their needs.',
    textCta: {
      _type: 'link',
      url: '/approach',
      text: 'learn more',
    },
  },
  banner2: {
    cta: {
      _type: 'cta',
      url: '/contact',
      text: 'Chat With Us',
    },
  },
};

const builder = imageUrlBuilder(sanityClient);
const Banner = dynamic(() => import('@/components/Banner'));
const CaseComponent = dynamic(() => import('@/components/CaseSection'));
const ClientLogoBar = dynamic(() => import('@/components/ClientLogoBar/ClientLogoBar'));
const CollapsibleText = dynamic(() => import('@/components/CollapsibleText'));
const Insights = dynamic(() => import('@/components/Insights'));
const Testimonials = dynamic(() => import('@/components/Testimonials'));
const Values = dynamic(() => import('@/components/Values'));

function urlFor(source) {
  return builder.image(source);
}

const Homepage = ({ menuData, footer, homeData, blogPosts }) => {
  const fold = true;
  const contentBlock = true;
  const testimonials = true;
  const collapsibleSection = true;
  const banner = false;
  const banner2 = true;
  const foldBackgroundImageUrl = homeData.headerImage
    ? urlFor(homeData.headerImage).width(1920).auto('format').quality(80).url()
    : hardcodedData.fold.image.hardCoded;

  return (
    <>
      {foldBackgroundImageUrl && (
        <Head>
          <link rel="preload" as="image" href={foldBackgroundImageUrl} />
        </Head>
      )}
      <MainLayout
        metaTitle={homeData.title}
        metaKeywords={homeData.metaKeywords}
        metaDescription={homeData.metaDescription}
        ogUrl="https://foxbox.com"
        isHomepage
        footer={footer}
        menuData={menuData}
        bgWhite
      >
        {fold && (
          <Fold
            heading={homeData.heroTitle || hardcodedData.fold.heading}
            bgImage={foldBackgroundImageUrl}
            textColor={homeData.heroTitleColor}
            coloredTail
          />
        )}
        {contentBlock && (
          <ContentBlock
            heading={homeData.introBodyTitle}
            content={homeData.introBodyText}
            headingColor={homeData.introBodyTitleColor?.hex}
            textColor={homeData.introBodyTextColor?.hex}
          />
        )}

        {homeData.clientLogoBar && <ClientLogoBar {...homeData.clientLogoBar} />}

        {homeData.case_1 && (
          <CaseComponent
            useGradient="bottom"
            gradientColor="#2F3F56"
            useMobileGradient
            bgPositionTwo
            bgImage={urlFor(homeData.case_1.caseCompanyBackgroudImage).width(1920).auto('format').quality(80).url()}
            logo={urlFor(homeData.case_1.caseCompanyLogo).width(200).auto('format').quality(80).url()}
            widerLogo={false}
            longContent={false}
            heading={homeData.case_1.caseHeadline}
            subheading={homeData.case_1.caseCompanyDescription}
            ctaUrl={homeData.case_1.caseCompanyUrl}
            ctaText="See the case study"
            layout="center"
            mark="key"
          />
        )}
        {homeData.case_2 && (
          <CaseComponent
            useGradient="left"
            gradientColor="#2F3F56"
            useMobileGradient
            bgImage={urlFor(homeData.case_2.caseCompanyBackgroudImage).width(1920).auto('format').quality(80).url()}
            logo={urlFor(homeData.case_2.caseCompanyLogo).width(200).auto('format').quality(80).url()}
            widerLogo={true}
            longContent={true}
            heading={homeData.case_2.caseHeadline}
            subheading={homeData.case_2.caseCompanyDescription}
            ctaUrl={homeData.case_2.caseCompanyUrl}
            ctaText="See the case study"
            layout="left"
            mark="stethoscope"
          />
        )}
        {homeData.case_3 && (
          <CaseComponent
            useGradient="right"
            gradientColor="#2F3F56"
            useMobileGradient
            bgPositionTwo
            bgImage={urlFor(homeData.case_3.caseCompanyBackgroudImage).width(1920).auto('format').quality(80).url()}
            logo={urlFor(homeData.case_3.caseCompanyLogo).width(200).auto('format').quality(80).url()}
            widerLogo={false}
            longContent={true}
            heading={homeData.case_3.caseHeadline}
            subheading={homeData.case_3.caseCompanyDescription}
            ctaUrl={homeData.case_3.caseCompanyUrl}
            ctaText="See the case study"
            layout="right"
            mark="gps"
          />
        )}
        {testimonials && homeData.testimonial && (
          <ResponsiveGrid style={{ padding: 0 }}>
            <Testimonials
              quoteline={homeData.testimonial.cite}
              author={homeData.testimonial.author}
              image={urlFor(homeData.testimonial.image).width(120).auto('format').quality(80).url()}
              extraText={homeData.testimonial.extraText}
              cta={homeData.testimonial.cta}
            />
          </ResponsiveGrid>
        )}

        {collapsibleSection && (
          <CollapsibleText
            titleText={homeData.collapsibleBox.heading}
            contentText={homeData.collapsibleBox.subheading}
            servicesList={homeData.collapsibleBox.items}
            image={urlFor(homeData.collapsibleBox.image).width(1920).auto('format').quality(80).url()}
            imageAlt={homeData.collapsibleBox.image?.alt ?? ''}
          />
        )}

        {banner && (
          <Banner
            primaryText={hardcodedData.banner1.heading}
            secondaryText={hardcodedData.banner1.content}
            textCta={hardcodedData.banner1.textCta}
            background="#F2F2F2"
            overlay={false}
          />
        )}

        {blogPosts && <Insights posts={blogPosts} heading="Inside the Box" />}

        {homeData.companyValues && <Values items={homeData.companyValues} />}

        {banner2 && (
          <Banner
            cta={hardcodedData.banner2.cta}
            background={`url(${urlFor(homeData.ctaBackgroundImage).width(1920).auto('format').quality(80).url()})`}
            primaryTextColor={homeData.ctaTitleColor}
            primaryText={homeData.ctaTitle}
            overlay={true}
            mode="dark"
          />
        )}
      </MainLayout>
    </>
  );
};

export default Homepage;
